/**
 * Cart page enhancement (<cart-items>).
 * Quantity changes hit /cart/change.js and re-render the cart section via
 * the Section Rendering API. Errors surface in a live alert instead of a
 * silent reload. Without JS the form posts to /cart normally.
 */
import { publish, PUB_SUB_EVENTS } from './global.js';

class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const input = event.target.closest('input[name="updates[]"]');
      if (input) this.updateLine(input.dataset.line, input.value);
      if (event.target.id === 'CartNote') this.updateNote(event.target.value);
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-line]');
      if (remove) {
        event.preventDefault();
        this.updateLine(remove.dataset.removeLine, 0);
      }
    });
  }

  get errorRegion() {
    return document.querySelector('[data-cart-error]');
  }

  showError(message) {
    const region = this.errorRegion;
    if (!region) return;
    region.hidden = false;
    region.querySelector('[data-cart-error-text]').textContent = message;
  }

  async updateLine(line, quantity) {
    if (this.errorRegion) this.errorRegion.hidden = true;
    try {
      const response = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      });
      const cart = await response.json();
      if (cart.status) {
        this.showError(cart.description || cart.message);
        await this.refresh();
        return;
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart });
      await this.refresh();
      const status = document.querySelector('[data-cart-status]');
      if (status) {
        const count = (window.shorelineStrings?.cartItemCount || '[count] items').replace('[count]', cart.item_count);
        const subtotal = document.querySelector('[data-cart-subtotal]')?.textContent.trim();
        status.textContent = [window.shorelineStrings?.cartUpdated || 'Cart updated.', count, subtotal].filter(Boolean).join(' ');
      }
    } catch (error) {
      this.showError(window.shorelineStrings?.cartUpdateError || 'Connection problem — your change was not saved. Please try again.');
    }
  }

  async updateNote(note) {
    try {
      await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      const status = document.querySelector('[data-note-status]');
      if (status) {
        status.textContent = window.shorelineStrings?.noteSaved || 'Note saved';
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => {
          status.textContent = '';
        }, 3000);
      }
    } catch (error) {
      this.showError(window.shorelineStrings?.noteError || 'Could not save the order note. Please try again.');
    }
  }

  async refresh() {
    const response = await fetch(`${window.routes.cart_url}?section_id=main-cart`);
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');
    const fresh = html.querySelector('[data-cart-contents]');
    const current = document.querySelector('[data-cart-contents]');
    if (!fresh || !current) return;
    // Capture immediately before replacement: do not steal focus if the
    // customer has already moved outside the cart while the request ran.
    const active = document.activeElement;
    const restore = current.contains(active);
    const row = active.closest('[data-cart-key]');
    const key = row?.dataset.cartKey;
    const line = Number(row?.dataset.line || 1);
    const name = active.getAttribute('name');
    const id = active.id;
    const removing = active.hasAttribute('data-remove-line');
    current.innerHTML = fresh.innerHTML;
    if (!restore) return;
    const rows = [...current.querySelectorAll('[data-cart-key]')];
    const sameRow = rows.find(el => el.dataset.cartKey === key);
    let target = id ? [...current.querySelectorAll('[id]')].find(el => el.id === id) : null;
    if (!target && sameRow) {
      target = removing ? sameRow.querySelector('[data-remove-line]')
        : [...sameRow.querySelectorAll('[name]')].find(el => el.getAttribute('name') === name);
    }
    if (!target && key) {
      const neighbor = rows[Math.min(line - 1, rows.length - 1)];
      target = neighbor?.querySelector('input[name="updates[]"]');
    }
    (target || current.querySelector('h1'))?.focus();
  }
}
if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);
